import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// Rdsn. — 1 T-shirt = 1 Flight Tag offert au choix.
(() => {
  if (window.__rdsnFlightTagGiftInstalled) return;
  window.__rdsnFlightTagGiftInstalled = true;

  const TSHIRT_PRODUCT_IDS = new Set([
    11009667793223,
    11050520215879,
    11050538402119,
    11050552295751,
    11050560586055,
  ]);

  const GIFT_VARIANTS = [
    {
      id: 54737434509639,
      title: 'Guadeloupe',
      code: '971',
      image: 'https://cdn.shopify.com/s/files/1/0992/8015/9047/files/ChatGPT_Image_25_aout_2026_21_00_02_2d87ac8b-673e-498c-90e5-b12b12288688.png?v=1787686231',
    },
    {
      id: 54737434542407,
      title: 'Martinique',
      code: '972',
      image: 'https://cdn.shopify.com/s/files/1/0992/8015/9047/files/ChatGPT_Image_25_aout_2026_20_57_56_c2de0b51-12e2-4e19-ab35-5be18d97d70f.png?v=1787686305',
    },
    {
      id: 54737434575175,
      title: 'Guyane',
      code: '973',
      image: 'https://cdn.shopify.com/s/files/1/0992/8015/9047/files/ChatGPT_Image_25_aout_2026_20_56_27_96dbfc4f-04cb-45f0-adf5-5f0a82f49ef4.png?v=1787686335',
    },
    {
      id: 54737434607943,
      title: 'Saint-Martin',
      code: '978',
      image: 'https://cdn.shopify.com/s/files/1/0992/8015/9047/files/ChatGPT_Image_25_aout_2026_21_04_55_e4851165-d5c8-40f1-aefb-47cea9f044b5.png?v=1787685529',
    },
    {
      id: 54737434640711,
      title: 'Saint-Barthélemy',
      code: '977',
      image: 'https://cdn.shopify.com/s/files/1/0992/8015/9047/files/ChatGPT_Image_25_aout_2026_20_56_31_22923365-075f-49e2-9673-bb87b602c86e.png?v=1787686369',
    },
  ];

  const root = window.Shopify?.routes?.root || '/';
  let busy = false;
  let refreshTimer = 0;
  let observer = null;

  function installStyles() {
    if (document.getElementById('rdsn-flight-gift-styles')) return;
    const style = document.createElement('style');
    style.id = 'rdsn-flight-gift-styles';
    style.textContent = `
      .rdsn-flight-gift {
        margin: 2px var(--cart-drawer-padding, 20px) 20px;
        padding: 14px;
        border: 1px solid rgb(var(--color-foreground-rgb, 23 23 23) / .14);
        border-radius: 12px;
        background: var(--color-background, #fff);
        color: var(--color-foreground, #171717);
      }
      .rdsn-flight-gift[hidden] { display: none !important; }
      .rdsn-flight-gift__head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 10px;
      }
      .rdsn-flight-gift__title {
        margin: 0;
        font-size: 13px;
        line-height: 1.25;
        font-weight: 700;
      }
      .rdsn-flight-gift__subtitle {
        margin: 3px 0 0;
        font-size: 11px;
        line-height: 1.35;
        color: rgb(var(--color-foreground-rgb, 23 23 23) / .62);
      }
      .rdsn-flight-gift__badge {
        flex: 0 0 auto;
        padding: 5px 8px;
        border-radius: 999px;
        background: #eef7f2;
        color: #0f6e56;
        font-size: 10px;
        line-height: 1;
        font-weight: 700;
      }
      .rdsn-flight-gift__choices {
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: minmax(108px, 1fr);
        gap: 8px;
        overflow-x: auto;
        overscroll-behavior-inline: contain;
        scrollbar-width: thin;
        padding-bottom: 3px;
      }
      .rdsn-flight-gift__choice {
        appearance: none;
        width: 100%;
        min-width: 0;
        padding: 7px;
        border: 1px solid rgb(var(--color-foreground-rgb, 23 23 23) / .16);
        border-radius: 10px;
        background: var(--color-background, #fff);
        color: inherit;
        text-align: left;
        cursor: pointer;
        transition: border-color .18s ease, transform .18s ease, opacity .18s ease;
      }
      .rdsn-flight-gift__choice:hover {
        border-color: rgb(var(--color-foreground-rgb, 23 23 23) / .55);
        transform: translateY(-1px);
      }
      .rdsn-flight-gift__choice:disabled { cursor: wait; opacity: .5; transform: none; }
      .rdsn-flight-gift__image-wrap {
        display: block;
        width: 100%;
        aspect-ratio: 4 / 3;
        overflow: hidden;
        border-radius: 7px;
        background: #f2f2f2;
        margin-bottom: 7px;
      }
      .rdsn-flight-gift__image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .rdsn-flight-gift__name {
        display: block;
        font-size: 10.5px;
        line-height: 1.2;
        font-weight: 700;
        min-height: 25px;
      }
      .rdsn-flight-gift__price {
        display: flex;
        align-items: baseline;
        gap: 5px;
        margin-top: 4px;
      }
      .rdsn-flight-gift__free { color: #0f6e56; font-size: 10px; font-weight: 700; }
      .rdsn-flight-gift__old { font-size: 9px; opacity: .48; text-decoration: line-through; }
      .rdsn-flight-gift__note {
        margin: 9px 0 0;
        font-size: 10px;
        line-height: 1.3;
        color: rgb(var(--color-foreground-rgb, 23 23 23) / .55);
      }
      .rdsn-flight-gift__error {
        margin: 8px 0 0;
        font-size: 10px;
        color: #b42318;
      }
      @media (max-width: 480px) {
        .rdsn-flight-gift { padding: 12px; }
        .rdsn-flight-gift__choices { grid-auto-columns: 104px; }
      }
    `;
    document.head.appendChild(style);
  }

  function createWidget() {
    const widget = document.createElement('section');
    widget.className = 'rdsn-flight-gift';
    widget.dataset.rdsnFlightGift = '';
    widget.hidden = true;
    widget.innerHTML = `
      <div class="rdsn-flight-gift__head">
        <div>
          <p class="rdsn-flight-gift__title">Ton Flight Tag offert</p>
          <p class="rdsn-flight-gift__subtitle">1 T-shirt = 1 Flight Tag au choix.</p>
        </div>
        <span class="rdsn-flight-gift__badge">OFFERT</span>
      </div>
      <div class="rdsn-flight-gift__choices"></div>
      <p class="rdsn-flight-gift__note">Précommande · expédition estimée sous environ 3 semaines.</p>
      <p class="rdsn-flight-gift__error" hidden></p>
    `;
    const choices = widget.querySelector('.rdsn-flight-gift__choices');
    GIFT_VARIANTS.forEach((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'rdsn-flight-gift__choice';
      button.dataset.variantId = String(variant.id);
      button.setAttribute('aria-label', `Choisir le Flight Tag ${variant.title} ${variant.code}`);
      button.innerHTML = `
        <span class="rdsn-flight-gift__image-wrap"><img class="rdsn-flight-gift__image" src="${variant.image}" alt="Flight Tag ${variant.title} ${variant.code}" loading="lazy"></span>
        <span class="rdsn-flight-gift__name">${variant.title}<br>${variant.code}</span>
        <span class="rdsn-flight-gift__price"><span class="rdsn-flight-gift__free">GRATUIT</span><span class="rdsn-flight-gift__old">5,00 €</span></span>
      `;
      button.addEventListener('click', () => addGift(variant.id, widget));
      choices?.appendChild(button);
    });
    return widget;
  }

  function mountWidget() {
    const content = document.querySelector('#cart-drawer .cart-drawer__content');
    const items = content?.querySelector(':scope > .cart-drawer__items');
    if (!content || !items) return null;

    let widget = content.querySelector(':scope > [data-rdsn-flight-gift]');
    if (!widget) {
      widget = createWidget();
      content.insertBefore(widget, items);
    }
    return widget;
  }

  function isGiftItem(item) {
    return item?.properties?._rdsn_gift === 'flight_tag';
  }

  function tshirtQuantity(cart) {
    return (cart?.items || []).reduce((total, item) => {
      return total + (TSHIRT_PRODUCT_IDS.has(Number(item.product_id)) ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function giftQuantity(cart) {
    return (cart?.items || []).reduce((total, item) => {
      return total + (isGiftItem(item) ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  function renderWidget(cart) {
    const widget = mountWidget();
    if (!widget) return;

    const shirts = tshirtQuantity(cart);
    const gifts = giftQuantity(cart);
    const remaining = Math.max(shirts - gifts, 0);
    widget.hidden = shirts === 0 || remaining === 0;
    if (widget.hidden) return;

    const title = widget.querySelector('.rdsn-flight-gift__title');
    const subtitle = widget.querySelector('.rdsn-flight-gift__subtitle');
    if (title) title.textContent = remaining === 1 ? 'Choisis ton Flight Tag offert' : `Choisis tes ${remaining} Flight Tags offerts`;
    if (subtitle) subtitle.textContent = shirts === 1 ? 'Ton T-shirt te donne droit à 1 Flight Tag au choix.' : `${shirts} T-shirts = ${shirts} Flight Tags offerts au total.`;
  }

  function setBusy(widget, state) {
    busy = state;
    widget?.querySelectorAll('.rdsn-flight-gift__choice').forEach((button) => {
      button.disabled = state;
    });
  }

  function showError(widget, message) {
    const error = widget?.querySelector('.rdsn-flight-gift__error');
    if (!error) return;
    error.textContent = message || 'Impossible d’ajouter le Flight Tag. Réessaie.';
    error.hidden = false;
  }

  async function addGift(variantId, widget) {
    if (busy) return;
    setBusy(widget, true);
    const error = widget?.querySelector('.rdsn-flight-gift__error');
    if (error) error.hidden = true;

    const before = await getCart().catch(() => null);
    if (!before || tshirtQuantity(before) <= giftQuantity(before)) {
      setBusy(widget, false);
      if (before) renderWidget(before);
      return;
    }

    const ids = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: { _rdsn_gift: 'flight_tag' },
          }],
          sections: ids.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add failed');

      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: result.sections,
          source: 'rdsn-flight-tag-gift',
          didError: false,
        },
      });
      renderWidget(cart);
    } catch (err) {
      deferred.reject(err);
      showError(widget, 'Impossible d’ajouter ce Flight Tag pour le moment.');
      console.warn('[Rdsn. Flight Tag gift]', err);
    } finally {
      setBusy(widget, false);
    }
  }

  async function removeExcessGifts(cart) {
    const shirts = tshirtQuantity(cart);
    const gifts = (cart?.items || []).filter(isGiftItem);
    let excess = giftQuantity(cart) - shirts;
    if (excess <= 0) return cart;

    const updates = {};
    for (let index = gifts.length - 1; index >= 0 && excess > 0; index -= 1) {
      const item = gifts[index];
      const qty = Number(item.quantity || 0);
      const removeQty = Math.min(qty, excess);
      updates[item.key] = qty - removeQty;
      excess -= removeQty;
    }

    const ids = sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: ids.join(','), sections_url: window.location.pathname }),
      });
      const updated = await response.json();
      if (!response.ok || updated.status) throw new Error(updated.description || updated.message || 'Update failed');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: {
          items: updated.items,
          itemCount: updated.item_count,
          sections: updated.sections,
          source: 'rdsn-flight-tag-gift-sync',
          didError: false,
        },
      });
      return updated;
    } catch (err) {
      deferred.reject(err);
      console.warn('[Rdsn. Flight Tag gift sync]', err);
      return cart;
    }
  }

  async function refresh({ enforce = true } = {}) {
    try {
      let cart = await getCart();
      if (enforce) cart = await removeExcessGifts(cart);
      renderWidget(cart);
    } catch (err) {
      console.warn('[Rdsn. Flight Tag gift refresh]', err);
    }
  }

  function scheduleRefresh(enforce = true) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh({ enforce }), 120);
  }

  function start() {
    installStyles();
    mountWidget();
    refresh();

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(() => scheduleRefresh(true)).catch(() => {});
    });

    observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
        return !target?.closest?.('[data-rdsn-flight-gift]');
      });
      if (relevant) scheduleRefresh(false);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
